import { useState } from 'react'
import { Maximize2, X } from 'lucide-react'
import type { Plaat as PlaatBlok, PlaatBron } from '../lib/types'

import startscherm from '../assets/platen/startscherm.webp'
import startscherm2x from '../assets/platen/startscherm@2x.webp'
import menu from '../assets/platen/menu.webp'
import menu2x from '../assets/platen/menu@2x.webp'
import tabbladen from '../assets/platen/tabbladen.webp'
import tabbladen2x from '../assets/platen/tabbladen@2x.webp'
import postvak from '../assets/platen/postvak.webp'
import postvak2x from '../assets/platen/postvak@2x.webp'

/* ===========================================================================
 *  Een plaat van het echte scherm
 *
 *  Met de aanwijzingen ERNAAST en niet eroverheen
 *  ----------------------------------------------
 *
 *  Nummertjes die op de afbeelding zelf staan, zijn de voor de hand liggende
 *  vorm -- en ze werken alleen op de breedte waarop ze zijn geplaatst. Op een
 *  telefoon schuift een bolletje van "menu linksboven" naar het midden van een
 *  knop die daar niet staat, en dan wijst hij iets anders aan dan bedoeld.
 *
 *  Hier staan ze eronder, genummerd, met de plaat erboven. Dat werkt op elke
 *  breedte, het is te lezen zonder de afbeelding te kunnen zien, en het is
 *  tekst -- dus corrigeerbaar, en voor te lezen.
 *
 *  Waarom hij uit te vergroten is
 *  ------------------------------
 *
 *  Een schermafdruk van een boekhoudscherm is op een telefoon van 390 pixels
 *  breed niet te lezen. De knop rechtsboven zet hem schermvullend; zonder dat
 *  is dit een plaatje waar mensen naar turen en dan doorklikken.
 * =========================================================================== */

const PLATEN: Record<PlaatBron, { een: string; twee: string }> = {
  startscherm: { een: startscherm, twee: startscherm2x },
  menu:        { een: menu,        twee: menu2x },
  tabbladen:   { een: tabbladen,   twee: tabbladen2x },
  postvak:     { een: postvak,     twee: postvak2x },
}

export default function Plaat({ blok }: { blok: PlaatBlok }) {
  const [groot, setGroot] = useState(false)
  const bron = PLATEN[blok.bron]

  if (!bron) return null

  return (
    <figure className="plaat">
      <div className="plaat-lijst">
        <img
          src={bron.een}
          srcSet={`${bron.een} 1x, ${bron.twee} 2x`}
          alt={blok.alt}
          /* lazy: deze dingen staan onder de vouw en wegen bij elkaar meer dan
             de hele app. Wie de module niet opent, haalt ze niet op. */
          loading="lazy"
          decoding="async"
        />
        <button
          type="button"
          className="plaat-groter"
          onClick={() => setGroot(true)}
          aria-label="Groter bekijken"
          title="Groter bekijken"
        >
          <Maximize2 size={16} />
        </button>
      </div>

      {blok.onderschrift && <figcaption>{blok.onderschrift}</figcaption>}

      {blok.wijzers && blok.wijzers.length > 0 && (
        <ol className="wijzers">
          {blok.wijzers.map((w, i) => (
            <li key={w.naam}>
              <span className="nr">{i + 1}</span>
              <span>
                <strong>{w.naam}</strong>
                <span className="wat">{w.wat}</span>
              </span>
            </li>
          ))}
        </ol>
      )}

      {groot && (
        <div
          className="plaat-vol"
          role="dialog"
          aria-label={blok.alt}
          onClick={() => setGroot(false)}
        >
          <button className="plaat-dicht" aria-label="Sluiten"><X size={20} /></button>
          <img src={bron.twee} alt={blok.alt} />
        </div>
      )}
    </figure>
  )
}
